import { ApiNetworkError, ApiParseError, ApiRequestError, errorFromResponse } from './errors.js';
function buildUrl(baseUrl, path, query) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
        if (value === undefined || value === null)
            continue;
        search.set(key, String(value));
    }
    const qs = search.toString();
    return `${baseUrl}${path}${qs ? `?${qs}` : ''}`;
}
/**
 * The single fetch wrapper both SPAs route every call through.
 *
 * Every success path is parsed with a `@proctoring/shared` schema rather than cast, so a
 * contract drift between the API and a frontend surfaces as a loud `ApiParseError` at the
 * boundary instead of an `undefined` deep inside a component tree.
 */
export class HttpClient {
    baseUrl;
    tokens;
    fetchImpl;
    onUnauthorized;
    constructor({ baseUrl, tokens, fetchImpl, onUnauthorized }) {
        this.baseUrl = baseUrl.replace(/\/$/, '');
        this.tokens = tokens;
        this.fetchImpl = fetchImpl ?? ((...args) => globalThis.fetch(...args));
        this.onUnauthorized = onUnauthorized;
    }
    /** The absolute base the session WebSocket URL is derived from. */
    get apiBaseUrl() {
        return this.baseUrl || globalThis.location?.origin || '';
    }
    async request(options) {
        const { method = 'GET', path, query, body, schema, anonymous, signal } = options;
        const url = buildUrl(this.baseUrl, path, query);
        // Every endpoint answers JSON, and saying so lets a dev proxy tell an API call apart
        // from a browser navigating to a client-side route that shares its path prefix.
        const headers = { accept: 'application/json' };
        if (body !== undefined)
            headers['content-type'] = 'application/json';
        if (!anonymous) {
            const token = this.tokens.get();
            if (token)
                headers.authorization = `Bearer ${token}`;
        }
        let response;
        try {
            response = await this.fetchImpl(url, {
                method,
                headers,
                ...(body === undefined ? {} : { body: JSON.stringify(body) }),
                ...(signal ? { signal } : {}),
            });
        }
        catch (cause) {
            // An aborted request is the caller's own doing (a cancelled query), not a network
            // fault — rethrowing it unwrapped keeps TanStack Query's cancellation handling intact.
            if (cause instanceof DOMException && cause.name === 'AbortError')
                throw cause;
            throw new ApiNetworkError(path, cause);
        }
        if (!response.ok) {
            const error = await errorFromResponse(response);
            if (error.isAuthFailure && !anonymous)
                this.onUnauthorized?.();
            throw error;
        }
        if (!schema)
            return undefined;
        let payload;
        try {
            payload = await response.json();
        }
        catch (cause) {
            throw new ApiParseError(path, [`response body was not JSON (${String(cause)})`]);
        }
        const parsed = schema.safeParse(payload);
        if (!parsed.success) {
            throw new ApiParseError(path, parsed.error.issues.map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`));
        }
        return parsed.data;
    }
    /**
     * Fetches an export endpoint as a `Blob`, bypassing schema parsing.
     *
     * The CSV variant of `/telemetry` returns `text/csv`, not an envelope, so downloads take
     * this path instead of `request`.
     */
    async requestBlob(options) {
        const { method = 'GET', path, query, signal } = options;
        const url = buildUrl(this.baseUrl, path, query);
        const headers = {};
        const token = this.tokens.get();
        if (token)
            headers.authorization = `Bearer ${token}`;
        let response;
        try {
            response = await this.fetchImpl(url, { method, headers, ...(signal ? { signal } : {}) });
        }
        catch (cause) {
            if (cause instanceof DOMException && cause.name === 'AbortError')
                throw cause;
            throw new ApiNetworkError(path, cause);
        }
        if (!response.ok) {
            const error = await errorFromResponse(response);
            if (error.isAuthFailure)
                this.onUnauthorized?.();
            throw error;
        }
        return response.blob();
    }
}
export { ApiNetworkError, ApiParseError, ApiRequestError };
//# sourceMappingURL=http.js.map